import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {modelConfigApi} from '../lib/api-resources';
import type {ActiveModelConfig, ActiveModelSetupRequest, ModelConfigResponse} from '../lib/types';
import './ModelSettingsPage.css';

const emptyForm: ActiveModelSetupRequest = {
  provider_name: '',
  provider_type: 'openai_compatible',
  base_url: '',
  api_key: '',
  model_id: '',
  display_name: '',
  vision: true,
};

const connectionLabel: Record<string, string> = {
  connected: '连接正常',
  checking: '正在检查',
  blocked: '暂时不可用',
  unknown: '尚未测试',
};

function formFromActive(active: ActiveModelConfig | null): ActiveModelSetupRequest {
  if (!active?.configured) return {...emptyForm};
  return {
    provider_name: active.provider_name || '',
    provider_type: active.provider_type || 'openai_compatible',
    base_url: active.base_url || '',
    api_key: '',
    model_id: active.model_id || '',
    display_name: active.display_name || '',
    vision: active.vision !== false,
  };
}

export default function ModelSettingsPage() {
  const [config, setConfig] = useState<ModelConfigResponse | null>(null);
  const [form, setForm] = useState<ActiveModelSetupRequest>({...emptyForm});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const next = await modelConfigApi.get();
      setConfig(next);
      if (next.active_model?.configured) setForm(formFromActive(next.active_model));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '模型配置暂时无法读取。');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  function updateForm<K extends keyof ActiveModelSetupRequest>(key: K, value: ActiveModelSetupRequest[K]) {
    setForm((current) => ({...current, [key]: value}));
  }

  async function saveAndUse() {
    if (!form.provider_name.trim() || !form.base_url.trim() || !form.model_id.trim()) {
      setError('请填写模型名称、API 地址和模型 ID。');
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const next = await modelConfigApi.configureActiveModel({
        ...form,
        provider_name: form.provider_name.trim(),
        base_url: form.base_url.trim(),
        model_id: form.model_id.trim(),
        display_name: form.display_name?.trim() || null,
        api_key: form.api_key?.trim() || null,
      });
      setConfig(next);
      setForm(formFromActive(next.active_model));
      setNotice('已保存，并已将这个模型设为所有建站 AI 任务使用的模型。');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '保存模型配置失败，请检查 API 信息。');
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    const providerId = config?.active_model?.provider_id;
    if (!providerId) return;
    setTesting(true);
    setError('');
    setNotice('');
    try {
      const result = await modelConfigApi.testProvider(providerId);
      setNotice(result.ok ? `连接测试成功（${Math.round(result.latency_ms)} ms）。` : `连接测试未通过：${result.message}`);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '连接测试失败。');
    } finally {
      setTesting(false);
    }
  }

  const active = config?.active_model;
  const routerReady = config?.router.status === 'connected';

  return <div className="workbench-page page model-settings-page">
    <header className="page-header model-page-header">
      <div>
        <div className="work-eyebrow">高级设置 · AI 与模型</div>
        <h1>AI 与模型</h1>
        <p className="page-subtitle">填入一个模型的 API 信息，保存后，建站规划、页面生成、内容生成和 Agent 都使用它。</p>
      </div>
      <div className="model-header-actions">
        <button className="model-secondary-button" type="button" onClick={() => void load()} disabled={loading}>{loading ? '读取中…' : '刷新状态'}</button>
        <Link className="work-link-button" to="/settings">返回高级设置</Link>
      </div>
    </header>

    {error && <div className="work-alert">{error}</div>}
    {notice && <div className="model-notice">{notice}</div>}
    {loading && !config && <section className="work-card model-loading"><p>正在读取模型配置…</p></section>}

    {config && <>
      <section className="model-simple-status work-card">
        <div>
          <div className="work-card-kicker">使用状态</div>
          <h2>{routerReady ? (active?.configured && active.enabled ? '模型已启用' : '还没有启用模型') : '模型服务尚未连接'}</h2>
          <p>{routerReady ? '保存后，所有 Site Builder AI 请求都会使用下方这个模型。模型密钥只在服务端保存。' : (config.router.message || '请先让系统管理员接通模型服务。')}</p>
        </div>
        <span className={'model-status-pill status-' + (active?.connection_status || (routerReady ? 'checking' : 'blocked'))}>
          {connectionLabel[active?.connection_status || (routerReady ? 'checking' : 'blocked')] || '暂不可用'}
        </span>
      </section>

      <section className="model-setup-section">
        <div className="model-section-heading">
          <div>
            <div className="work-card-kicker">模型信息</div>
            <h2>配置一个模型</h2>
            <p>API Key 只用于保存到现有模型服务，页面不会显示明文。更新已有模型时，API Key 留空即可保留原密钥。</p>
          </div>
          {active?.configured && <span className="model-active-badge">当前正在使用</span>}
        </div>

        <div className="model-form-grid">
          <label>
            <span>服务名称</span>
            <input value={form.provider_name} onChange={(event) => updateForm('provider_name', event.target.value)} placeholder="例如：我的视觉模型" autoComplete="off" />
            <small>给自己看的名称。</small>
          </label>
          <label>
            <span>模型名称（可选）</span>
            <input value={form.display_name || ''} onChange={(event) => updateForm('display_name', event.target.value)} placeholder="例如：Qwen3-VL" autoComplete="off" />
            <small>不填时使用模型 ID。</small>
          </label>
          <label className="model-form-wide">
            <span>API 地址</span>
            <input type="url" value={form.base_url} onChange={(event) => updateForm('base_url', event.target.value)} placeholder="https://你的模型服务/v1" autoComplete="url" />
            <small>支持 OpenAI 兼容接口；请填服务的 API 基地址。</small>
          </label>
          <label>
            <span>模型 ID</span>
            <input value={form.model_id} onChange={(event) => updateForm('model_id', event.target.value)} placeholder="例如：qwen-vl-max" autoComplete="off" />
            <small>服务商提供的实际 model 名称。</small>
          </label>
          <label>
            <span>API Key</span>
            <input type="password" value={form.api_key || ''} onChange={(event) => updateForm('api_key', event.target.value)} placeholder={active?.has_api_key ? '已保存，留空即可' : '输入 API Key'} autoComplete="new-password" />
            <small>{active?.has_api_key ? '当前已有密钥，页面只显示是否存在。' : '密钥会加密保存，不会返回。'}</small>
          </label>
        </div>

        <label className="model-vision-toggle">
          <input type="checkbox" checked={form.vision} onChange={(event) => updateForm('vision', event.target.checked)} />
          <span><strong>这是视觉模型</strong><small>用于读取产品图片和其他图片内容。</small></span>
        </label>

        <details className="model-format-details">
          <summary>接口格式</summary>
          <label>
            <span>接口类型</span>
            <select value={form.provider_type} onChange={(event) => updateForm('provider_type', event.target.value)}>
              <option value="openai_compatible">OpenAI 兼容接口</option>
              <option value="qwen">Qwen 接口</option>
              <option value="deepseek">DeepSeek 接口</option>
              <option value="anthropic">Anthropic 接口</option>
              <option value="gemini">Gemini 接口</option>
            </select>
          </label>
        </details>

        <div className="model-form-actions">
          <button className="work-primary-button" type="button" onClick={() => void saveAndUse()} disabled={saving || !routerReady}>{saving ? '保存中…' : '保存并使用这个模型'}</button>
          {active?.configured && <button className="model-secondary-button" type="button" onClick={() => void testConnection()} disabled={testing || !active.provider_id}>{testing ? '测试中…' : '测试连接'}</button>}
        </div>
      </section>

      {active?.configured && <section className="model-current-section work-card">
        <div className="model-section-heading">
          <div>
            <div className="work-card-kicker">当前模型</div>
            <h2>{active.display_name || active.model_id}</h2>
            <p>{active.provider_name} · {active.model_id}</p>
          </div>
          <span className="model-current-check">✓ 所有建站 AI 使用此模型</span>
        </div>
        <div className="model-current-facts">
          <span>API：{active.host || '已配置'}</span>
          <span>凭据：{active.has_api_key ? '已配置（已遮罩）' : '未配置'}</span>
          <span>视觉理解：{active.vision ? '支持' : '未启用'}</span>
        </div>
      </section>}

      <section className="model-image-note"><strong>图片生成</strong><span>图片生成继续使用现有图片服务，不需要在这里配置。</span></section>
    </>}
  </div>;
}
